import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import type { AppState } from '../store';

/**
 * Antibody Analysis State
 *
 * This slice manages ALL UI state for the antibody analysis feature.
 * State persists across navigation (when user goes to Network Map and back).
 *
 * RTK:
 * - State needs to persist when navigating between routes
 * - Redux-persist automatically saves/restores state
 * - Consistent with existing clonotypes.slice.ts pattern
 * - Reduces prop drilling while maintaining persistence
 */
export interface AntibodyAnalysisState {
  // ========== Display Settings ==========
  // Controls whether Framework Regions (FWR1-4) are shown
  showFWR: boolean;

  // Which regions are currently highlighted (fwr1, cdr1, fwr2, cdr2, fwr3, cdr3, fwr4)
  // Multiple regions can be active simultaneously via toggleHighlightedRegion
  highlightedRegions: string[];

  // Search query for filtering clonotypes/sequences
  searchQuery: string;

  // Whether to hide singleton clonotypes (clonotypes with only 1 sequence)
  hideSingletons: boolean;

  // Whether to show per-residue liability annotations on sequences
  showLiabilities: boolean;

  // Whether to show SHM (Somatic Hypermutation) annotations on sequences
  showSHM: boolean;

  // ========== Selection State ==========
  // Selected sequences for comparison/filtering (format: "clonotypeId-sequenceId")
  // Stored as array instead of Set because Redux requires serializable state
  selectedSequences: string[];

  // Whether the filter is active (showing only selected sequences)
  filterActive: boolean;

  // ========== Filter Settings ==========
  // Which chain type to display: all, heavy (IGH), or light (IGK/IGL)
  chainType: 'all' | 'heavy' | 'light';

  // Selected liability bin for filtering (-2 to 0, or null for no filter)
  selectedLiability: number | null;

  // ========== UI State ==========
  // Scroll position to restore when user returns to this view
  scrollPosition: number;

  // Which clonotypes are explicitly collapsed in the table.
  // Absence of a key means the row is expanded (open by default).
  // Record<string, boolean> where key is row ID (e.g., 'c_1', 'c_2')
  collapsedClonotypes: Record<string, boolean>;
}

const initialState: AntibodyAnalysisState = {
  // Display defaults
  showFWR: false,
  highlightedRegions: ['cdr3'],
  searchQuery: '',
  hideSingletons: false,
  showLiabilities: true,
  showSHM: false,

  // Selection defaults
  selectedSequences: [],
  filterActive: false,

  // Filter defaults
  chainType: 'all',
  selectedLiability: null,

  // UI defaults
  scrollPosition: 0,
  collapsedClonotypes: {},
};

const antibodyAnalysisSlice = createSlice({
  name: 'antibodyAnalysis',
  initialState,
  reducers: {
    // ========== Display Actions ==========
    setShowFWR: (state, action: PayloadAction<boolean>) => {
      state.showFWR = action.payload;
    },

    /**
     * Toggle a region in/out of the highlighted set.
     * Passing the same key twice de-selects it; a new key is appended.
     */
    toggleHighlightedRegion: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      if (key === 'cdr3') {
        return;
      } // CDR3 is always on and cannot be toggled off
      // Guard against stale persisted state where highlightedRegions may be undefined
      if (!state.highlightedRegions) {
        state.highlightedRegions = ['cdr3'];
      }
      const idx = state.highlightedRegions.indexOf(key);
      if (idx > -1) {
        state.highlightedRegions.splice(idx, 1);
      } else {
        state.highlightedRegions.push(key);
      }
    },

    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },

    setHideSingletons: (state, action: PayloadAction<boolean>) => {
      state.hideSingletons = action.payload;
    },

    setShowLiabilities: (state, action: PayloadAction<boolean>) => {
      state.showLiabilities = action.payload;
    },

    setShowSHM: (state, action: PayloadAction<boolean>) => {
      state.showSHM = action.payload;
    },

    // ========== Selection Actions ==========
    /**
     * Toggle a sequence selection
     * @param action.payload - Sequence key in format "clonotypeId-sequenceId"
     */
    toggleSequence: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      const index = state.selectedSequences.indexOf(key);

      if (index > -1) {
        // Already selected - remove it
        state.selectedSequences.splice(index, 1);
      } else {
        // Not selected - add it
        state.selectedSequences.push(key);
      }
    },

    /**
     * Clear all selections and deactivate filter
     */
    clearSelection: (state) => {
      state.selectedSequences = [];
      state.filterActive = false;
    },

    /**
     * Set multiple selected sequences at once
     * @param action.payload - Array of sequence keys in format "clonotypeId-sequenceId"
     */
    setSelectedSequences: (state, action: PayloadAction<string[]>) => {
      state.selectedSequences = action.payload;
    },

    setFilterActive: (state, action: PayloadAction<boolean>) => {
      state.filterActive = action.payload;
    },

    // ========== Filter Actions ==========
    /**
     * Set chain type filter
     * Automatically clears selections when changing chain type
     */
    setChainType: (state, action: PayloadAction<'all' | 'heavy' | 'light'>) => {
      // Chain type only changes which chain's columns are shown — a selected sequence
      // (clonotypeId-sequenceId) is the same entity in every view, so the selection is
      // preserved across chain switches. (Clearing it here previously wiped the whole
      // selection on a view toggle and desynced it from the clonotype selection.)
      state.chainType = action.payload;
    },

    setSelectedLiability: (state, action: PayloadAction<number | null>) => {
      state.selectedLiability = action.payload;
    },

    // ========== UI Actions ==========
    setScrollPosition: (state, action: PayloadAction<number>) => {
      state.scrollPosition = action.payload;
    },

    /**
     * Set which clonotypes are explicitly collapsed.
     * An empty record means all rows are expanded (the default).
     */
    setCollapsedClonotypes: (state, action: PayloadAction<Record<string, boolean>>) => {
      state.collapsedClonotypes = action.payload;
    },

    toggleClonotypeCollapsed: (state, action: PayloadAction<string>) => {
      const rowId = action.payload;
      if (state.collapsedClonotypes[rowId]) {
        delete state.collapsedClonotypes[rowId];
      } else {
        state.collapsedClonotypes[rowId] = true;
      }
    },

    /** Set the collapsed flag for a single row by id (see toggleClonotypeCollapsed rationale). */
    setClonotypeCollapsedFlag: (state, action: PayloadAction<{ rowId: string; collapsed: boolean }>) => {
      const { rowId, collapsed } = action.payload;
      if (collapsed) {
        state.collapsedClonotypes[rowId] = true;
      } else {
        delete state.collapsedClonotypes[rowId];
      }
    },

    // ========== Reset Action ==========
    /**
     * Reset all antibody analysis state to defaults
     * Useful when user wants to start fresh
     */
    resetAntibodyAnalysis: () => initialState,
  },
});

// ========== Actions Export ==========
export const {
  setShowFWR,
  toggleHighlightedRegion,
  setSearchQuery,
  setHideSingletons,
  setShowLiabilities,
  setShowSHM,
  toggleSequence,
  clearSelection,
  setSelectedSequences,
  setFilterActive,
  setChainType,
  setSelectedLiability,
  setScrollPosition,
  setCollapsedClonotypes,
  toggleClonotypeCollapsed,
  setClonotypeCollapsedFlag,
  resetAntibodyAnalysis,
} = antibodyAnalysisSlice.actions;

// ========== Selectors ==========
/**
 * Selectors allow components to subscribe to specific pieces of state
 * Components only re-render when their selected state changes
 */

// Display selectors
export const selectShowFWR = (state: AppState) => state.antibodyAnalysis.showFWR;
export const selectHighlightedRegions = (state: AppState) => state.antibodyAnalysis.highlightedRegions ?? [];
export const selectSearchQuery = (state: AppState) => state.antibodyAnalysis.searchQuery;
export const selectHideSingletons = (state: AppState) => state.antibodyAnalysis.hideSingletons;
export const selectShowLiabilities = (state: AppState) => state.antibodyAnalysis.showLiabilities;
export const selectShowSHM = (state: AppState) => state.antibodyAnalysis.showSHM;

// Selection selectors
export const selectSelectedSequences = (state: AppState) => state.antibodyAnalysis.selectedSequences;
export const selectFilterActive = (state: AppState) => state.antibodyAnalysis.filterActive;

/**
 * Memoized selector that converts selectedSequences array to Set
 * Used this in components for O(1) lookup performance when checking if sequence is selected
 */
export const selectSelectedSequencesSet = (state: AppState): Set<string> => new Set(state.antibodyAnalysis.selectedSequences);

// Filter selectors
export const selectChainType = (state: AppState) => state.antibodyAnalysis.chainType;
export const selectSelectedLiability = (state: AppState) => state.antibodyAnalysis.selectedLiability;

// UI selectors
export const selectScrollPosition = (state: AppState) => state.antibodyAnalysis.scrollPosition;
// ?? {} guards against stale persisted state that predates this field
export const selectCollapsedClonotypes = (state: AppState) => state.antibodyAnalysis.collapsedClonotypes ?? {};

// ========== Reducer Export ==========
export default antibodyAnalysisSlice.reducer;
